

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import EnquiryForm from "./EnquiryForm";
import ConvertEnquiryForm from "./ConvertEnquiryForm";
import EnquiryDetailModal from "./EnquiryDetailModal";
import { enquiriesApi, clientsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { 
  EnquiryHeader, 
  EnquiryFilters, 
  EnquiryStats, 
  EnquiryList,
  EnquiryData 
} from "./components";

const statuses = ["all", "new", "qualified", "converted", "rejected"] as const;
type Status = typeof statuses[number];

// Alias EnquiryData as Enquiry for backwards compatibility
type Enquiry = EnquiryData;

interface Client {
  id: number;
  name: string;
  phone?: string;
  email?: string;
}

export default function EnquiriesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const canDelete = user?.role === 'admin' || user?.role === 'master' || user?.role === 'super_admin';
  
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<Status>("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailEnquiryId, setDetailEnquiryId] = useState<number | null>(null);
  const [editingEnquiry, setEditingEnquiry] = useState<Enquiry | null>(null);
  const [convertingEnquiry, setConvertingEnquiry] = useState<Enquiry | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadEnquiries();
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsApi.list();
      const clientsData = response.data?.data || response.data || [];
      setClients(clientsData);
    } catch (error) {
      console.error("Error loading clients:", error);
    }
  };

  const loadEnquiries = async () => {
    try {
      setLoading(true);
      const response = await enquiriesApi.list();
      const enquiriesData = response.data?.data || response.data || [];
      setEnquiries(enquiriesData);
    } catch (error) {
      showToast("Failed to load enquiries", "error");
      console.error("Error loading enquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  const statusCounts = useMemo(() => {
    const counts: Record<Status, number> = { 
      all: enquiries.length, 
      new: enquiries.filter(e => e.status === 'new').length, 
      qualified: enquiries.filter(e => e.status === 'qualified').length, 
      converted: enquiries.filter(e => e.status === 'converted').length, 
      rejected: enquiries.filter(e => e.status === 'rejected').length 
    };
    return counts;
  }, [enquiries]);

  const filtered = useMemo(() => {
    return enquiries.filter(e => {
      const matchStatus = activeFilter === "all" || e.status === activeFilter;
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || 
        e.name?.toLowerCase().includes(q) || 
        e.event_type?.toLowerCase().includes(q) || 
        e.email?.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [activeFilter, searchQuery, enquiries]);

  // Map enquiry data to form data
  const mapToFormData = (enquiry: any) => ({
    client: enquiry.client_name || enquiry.client?.name || enquiry.name || "",
    contact: enquiry.client_phone || enquiry.client?.phone || enquiry.phone || "",
    email: enquiry.client_email || enquiry.client?.email || enquiry.email || "",
    type: enquiry.type || enquiry.event_type || "",
    guests: (enquiry.guest_count || enquiry.guests)?.toString() || "",
    date: enquiry.event_date || "",
    leadSource: enquiry.lead_source || "",
    status: enquiry.status || "new",
    notes: enquiry.notes || "",
  });

  // Map form data to API data
  const mapToApiData = (formData: any, clientId: number | undefined) => ({
    client_id: clientId || 0,
    client_name: formData.client, // Keep these as fallback just in case backend takes them
    client_phone: formData.contact,
    client_email: formData.email,
    event_type: formData.type, // Back-end expects event_type natively
    type: formData.type, // Sometimes backend falls back to this
    guest_count: parseInt(formData.guests) || 0,
    event_date: formData.date || null,
    enquiry_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(), // Backend expects enquiry_date!
    lead_source: formData.leadSource,
    status: formData.status,
    notes: formData.notes,
  });

  // Helper to ensure a client exists
  const ensureClient = async (formData: any) => {
    try {
      // First, try to find an existing client securely via search
      const searchTerm = formData.contact || formData.email || formData.client;
      if (searchTerm) {
        const searchRes = await clientsApi.list(`search=${encodeURIComponent(searchTerm)}`);
        const existingClients = searchRes.data?.data || searchRes.data || [];
        if (existingClients.length > 0) {
           return existingClients[0].id;
        }
      }

      // If no client was found by search, proceed to create one
      const newClientResponse = await clientsApi.create({
        name: formData.client || "Unknown Client",
        phone: formData.contact || "",
        email: formData.email || "",
        status: "active"
      });
      const clientId = newClientResponse.data?.data?.id || newClientResponse.data?.id || newClientResponse?.id;
      if (!clientId) throw new Error("Could not retrieve new client ID");
      return clientId;
    } catch (err) {
      console.error("Failed to implicitly create client:", err);
      throw err;
    }
  };

  // Add new enquiry
  const handleAddEnquiry = async (formData: any) => {
    setSaving(true);
    try {
      const clientId = await ensureClient(formData);
      await enquiriesApi.create(mapToApiData(formData, clientId));
      showToast("Enquiry created successfully", "success");
      setIsAddModalOpen(false);
      loadEnquiries();
    } catch (error: any) {
      const message = error?.message || "Failed to create enquiry";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  // Edit enquiry
  const handleEditEnquiry = async (formData: any) => {
    if (!editingEnquiry) return;
    setSaving(true);
    try {
      // For updates, the client might already belong to the editingEnquiry (client_id). 
      // If we don't have it explicitly bound to formData, we can just ensure it exists again here.
      let clientId = (editingEnquiry as any).client_id;
      if (!clientId) {
         clientId = await ensureClient(formData);
      }
      await enquiriesApi.update(editingEnquiry.id, mapToApiData(formData, clientId));
      showToast("Enquiry updated successfully", "success");
      setIsEditModalOpen(false);
      setEditingEnquiry(null);
      loadEnquiries();
    } catch (error: any) {
      const message = error?.message || "Failed to update enquiry";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this enquiry?")) return;
    try {
      await enquiriesApi.delete(id);
      showToast("Enquiry deleted successfully", "success");
      loadEnquiries();
    } catch (error) {
      showToast("Failed to delete enquiry", "error");
    }
  };

  const handleConvert = async (data: any) => {
    if (!convertingEnquiry) return;
    try {
      setSaving(true);
      
      let clientId = data.client_id;

      // Auto-create client if not selected
      if (!clientId) {
        try {
          const newClientResponse = await clientsApi.create({
            name: convertingEnquiry.client_name || convertingEnquiry.name || "Unknown Client",
            phone: convertingEnquiry.client_phone || convertingEnquiry.phone || "",
            email: convertingEnquiry.client_email || convertingEnquiry.email || "",
            status: "active"
          });
          clientId = newClientResponse.data?.data?.id || newClientResponse.data?.id || newClientResponse?.id;
          
          if (!clientId) {
             throw new Error("Could not retrieve new client ID");
          }
        } catch (err: any) {
          showToast(err?.message || "Failed to auto-create client", "error");
          setSaving(false);
          return;
        }
      }

      // Create quotation with minimal data
      const { data: responseData } = await enquiriesApi.convertToQuotation(convertingEnquiry.id, {
        client_id: clientId,
        event_name: data.event_name,
        event_date: data.event_date,
        event_type: data.event_type,
        guests: parseInt(data.guests),
      });
      
      const quotationId = responseData?.data?.quotation?.id;
      showToast("Quotation created successfully", "success");
      setIsConvertModalOpen(false);
      setConvertingEnquiry(null);
      loadEnquiries();
      
      // Redirect to quotation edit page with enquiry data pre-filled
      if (quotationId) {
        navigate(`/quotations?action=edit&id=${quotationId}`);
      } else {
        navigate("/quotations");
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Failed to convert enquiry";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (enquiry: Enquiry) => {
    setEditingEnquiry(enquiry);
    setIsEditModalOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <EnquiryHeader onAddClick={() => setIsAddModalOpen(true)} />

      {/* Filters */}
      <EnquiryFilters
        statuses={statuses}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusCounts={statusCounts}
      />

      {/* Stats */}
      <EnquiryStats
        totalCount={enquiries.length}
        newCount={statusCounts.new}
        qualifiedCount={statusCounts.qualified}
        convertedCount={statusCounts.converted}
      />

      {/* List (Table + Cards) */}
      <EnquiryList
        enquiries={filtered}
        isStaff={isStaff}
        canDelete={canDelete}
        onEdit={openEditModal}
        onConvert={(e) => { setConvertingEnquiry(e); setIsConvertModalOpen(true); }}
        onDelete={handleDelete}
        onViewDetails={(id) => { setDetailEnquiryId(id); setIsDetailModalOpen(true); }}
      />

      {/* Add Enquiry Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Enquiry"
        size="md"
      >
        <EnquiryForm
          onSubmit={handleAddEnquiry}
          onCancel={() => setIsAddModalOpen(false)}
        />
      </Modal>

      {/* Edit Enquiry Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingEnquiry(null); }}
        title="Edit Enquiry"
        size="md"
      >
        {editingEnquiry && (
          <EnquiryForm
            initialData={mapToFormData(editingEnquiry)}
            isEdit={true}
            onSubmit={handleEditEnquiry}
            onCancel={() => { setIsEditModalOpen(false); setEditingEnquiry(null); }}
          />
        )}
      </Modal>

      {/* Convert Enquiry Modal */}
      <Modal
        isOpen={isConvertModalOpen}
        onClose={() => { setIsConvertModalOpen(false); setConvertingEnquiry(null); }}
        title="Convert Enquiry to Quotation"
        size="md"
      >
        {convertingEnquiry && (
          <ConvertEnquiryForm
            enquiry={convertingEnquiry}
            clients={clients}
            onSubmit={handleConvert}
            onCancel={() => { setIsConvertModalOpen(false); setConvertingEnquiry(null); }}
          />
        )}
      </Modal>

      {/* Detail Modal */}
      <EnquiryDetailModal
        isOpen={isDetailModalOpen}
        enquiryId={detailEnquiryId}
        onClose={() => { setIsDetailModalOpen(false); setDetailEnquiryId(null); }}
        onEnquiryUpdated={loadEnquiries}
      />
    </DashboardLayout>
  );
}
